import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { Save, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatDate } from '../../lib/format';

export default function VendorProfile() {
  const { vendorFetch } = useVendorAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState([]);
  // Change password
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const load = async () => {
    try {
      const p = await vendorFetch('/partner/profile');
      setProfile(p);
      setForm({
        contact_person: p.contact_person || '',
        phone: p.phone || '',
        address: p.address || '',
        city: p.city || '',
        state: p.state || '',
        pincode: p.pincode || '',
        // sensitive — change request
        name: p.name || '',
        gstin: p.gstin || '',
        pan: p.pan || '',
        bank_name: p.bank_name || '',
        bank_account: p.bank_account || '',
        bank_ifsc: p.bank_ifsc || '',
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load profile'); }
    try {
      const cr = await vendorFetch('/partner/profile/changes');
      setChanges(cr.items || []);
    } catch { /* optional */ }
  };

  useEffect(() => { load(); }, []);

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('New passwords do not match'); return; }
    if (pwForm.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setPwLoading(true);
    try {
      await vendorFetch('/partner/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }),
      });
      toast.success('Password updated successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePw(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Password change failed'); }
    setPwLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await vendorFetch('/partner/profile', {
        method: 'PUT', body: JSON.stringify(form),
      });
      toast.success(res.change_request_created
        ? 'Sensitive field changes submitted for procurement approval.'
        : 'Profile updated.');
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    setSaving(false);
  };

  if (!profile) return <div className="text-slate-400 py-8">Loading…</div>;

  const DIRECT_FIELDS = [
    { key: 'contact_person', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'PIN Code' },
  ];

  const SENSITIVE_FIELDS = [
    { key: 'name', label: 'Company Name' },
    { key: 'gstin', label: 'GSTIN' },
    { key: 'pan', label: 'PAN' },
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'bank_account', label: 'Bank Account Number' },
    { key: 'bank_ifsc', label: 'IFSC Code' },
  ];

  return (
    <div className="space-y-5 max-w-3xl" data-testid="vendor-profile-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-bold text-xl text-slate-900">My Profile</h1>
          <p className="text-sm text-slate-500 mt-0.5">{profile.vendor_code} · {profile.name}</p>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)}
            className="px-4 py-2 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-bold hover:bg-[#3b4872]/5">
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); load(); }} className="px-3 py-2 border border-slate-300 rounded-sm text-xs text-slate-600 font-bold">Cancel</button>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Direct-edit fields */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="font-bold text-sm text-slate-900 mb-4">Contact Details <span className="text-xs font-normal text-slate-400 ml-2">— updates immediately</span></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {DIRECT_FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{f.label}</label>
              {editing
                ? <input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                    data-testid={`profile-${f.key}`} />
                : <div className="text-sm text-slate-700">{profile[f.key] || '—'}</div>
              }
            </div>
          ))}
        </div>
      </div>

      {/* Sensitive fields */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="font-bold text-sm text-slate-900">Registered Details</div>
          {editing && (
            <span className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-sm font-semibold">
              <AlertTriangle size={11} /> Changes require procurement approval
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {SENSITIVE_FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{f.label}</label>
              {editing
                ? <input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                    data-testid={`profile-${f.key}`} />
                : <div className="text-sm text-slate-700">{profile[f.key] || '—'}</div>
              }
            </div>
          ))}
        </div>
      </div>

      {/* Pending change requests */}
      {changes.filter(c => c.status === 'pending').length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-2">
          <div className="font-bold text-sm text-slate-900">Pending Changes (awaiting procurement approval)</div>
          {changes.filter(c => c.status === 'pending').map((c, i) => (
            <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-sm px-3 py-2">
              <span className="font-semibold">{Object.keys(c.requested_changes || {}).join(', ')}</span>
              {' '}submitted {formatDate(c.requested_at)}
            </div>
          ))}
        </div>
      )}
      {/* Security — Change Password */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <button onClick={() => setShowChangePw(!showChangePw)} type="button"
          className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-slate-50"
          data-testid="toggle-change-pw">
          <div>
            <div className="font-bold text-sm text-slate-900">Security</div>
            <div className="text-xs text-slate-500 mt-0.5">Change your portal password</div>
          </div>
          <span className="text-[#3b4872] text-xs font-bold">{showChangePw ? 'Cancel' : 'Change Password'}</span>
        </button>
        {showChangePw && (
          <form onSubmit={handleChangePw} className="border-t border-slate-100 px-5 py-4 space-y-3 bg-slate-50">
            {[
              { key: 'current_password', label: 'Current Password' },
              { key: 'new_password', label: 'New Password' },
              { key: 'confirm_password', label: 'Confirm New Password' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
                <input type="password" value={pwForm[key]}
                  onChange={e => setPwForm({ ...pwForm, [key]: e.target.value })}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                  required data-testid={`change-pw-${key}`} />
              </div>
            ))}
            <button type="submit" disabled={pwLoading}
              className="px-5 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold disabled:opacity-50 hover:bg-[#2d3856]">
              {pwLoading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
